import React from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, MultiSelect } from "@/components";
import type { ContentStrategyConfigDemographics } from "../interfaces";

const AGE_GROUP_OPTIONS = [
  { value: "13-17", label: "13-17" },
  { value: "18-24", label: "18-24" },
  { value: "25-34", label: "25-34" },
  { value: "35-44", label: "35-44" },
  { value: "45-54", label: "45-54" },
  { value: "55-64", label: "55-64" },
  { value: "65+", label: "65+" },
];

const REGION_OPTIONS = [
  { value: "North America", label: "North America" },
  { value: "South America", label: "South America" },
  { value: "Europe", label: "Europe" },
  { value: "Asia Pacific", label: "Asia Pacific" },
  { value: "Middle East", label: "Middle East" },
  { value: "Africa", label: "Africa" },
  { value: "Oceania", label: "Oceania" },
];

const ETHNICITY_OPTIONS = [
  { value: "African American", label: "African American" },
  { value: "Asian", label: "Asian" },
  { value: "Caucasian", label: "Caucasian" },
  { value: "Hispanic/Latino", label: "Hispanic/Latino" },
  { value: "Middle Eastern", label: "Middle Eastern" },
  { value: "Native American", label: "Native American" },
  { value: "Pacific Islander", label: "Pacific Islander" },
  { value: "Other", label: "Other" },
];

const INTEREST_OPTIONS = [
  { value: "Technology", label: "Technology" },
  { value: "Sports", label: "Sports" },
  { value: "Music", label: "Music" },
  { value: "Travel", label: "Travel" },
  { value: "Fashion", label: "Fashion" },
  { value: "Food & Cooking", label: "Food & Cooking" },
  { value: "Health & Fitness", label: "Health & Fitness" },
  { value: "Arts & Culture", label: "Arts & Culture" },
  { value: "Gaming", label: "Gaming" },
  { value: "Reading", label: "Reading" },
  { value: "Entertainment", label: "Entertainment" },
  { value: "Business", label: "Business" },
  { value: "Education", label: "Education" },
  { value: "Lifestyle", label: "Lifestyle" },
  { value: "Photography", label: "Photography" },
];

const demographicsSchema = yup.object({
  ageGroups: yup
    .array()
    .of(yup.string())
    .min(1, "At least one age group is required")
    .required("Age group is required"),
  regions: yup
    .array()
    .of(yup.string())
    .min(1, "At least one region is required")
    .required("Region is required"),
  ethnicities: yup.array().of(yup.string()).optional(),
  interests: yup
    .array()
    .of(yup.string())
    .min(1, "At least one interest is required")
    .required("At least one interest is required"),
});

type DemographicsFormData = {
  ageGroups: string[];
  regions: string[];
  ethnicities: string[];
  interests: string[];
};

interface DemographicsConfigStepProps {
  initialDemographics?: Partial<ContentStrategyConfigDemographics>;
  onNext: (demographics: ContentStrategyConfigDemographics) => void;
  onBack: () => void;
}

export const DemographicsConfigStep: React.FC<DemographicsConfigStepProps> = ({
  initialDemographics,
  onNext,
  onBack,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DemographicsFormData>({
    resolver: yupResolver(demographicsSchema) as any,
    defaultValues: {
      ageGroups: initialDemographics?.ageGroups ?? [],
      regions: initialDemographics?.regions ?? [],
      ethnicities: initialDemographics?.ethnicities ?? [],
      interests: initialDemographics?.interests ?? [],
    },
  });

  const onFormSubmit = (data: DemographicsFormData) => {
    onNext({
      ageGroups: data.ageGroups,
      regions: data.regions,
      ...(data.ethnicities?.length ? { ethnicities: data.ethnicities } : undefined),
      interests: data.interests,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="ageGroups"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label="Age Group *"
              options={AGE_GROUP_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              placeholder="Select age groups"
              error={!!errors.ageGroups}
              helperText={errors.ageGroups?.message}
            />
          )}
        />
        <Controller
          name="regions"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label="Region *"
              options={REGION_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              placeholder="Select regions"
              error={!!errors.regions}
              helperText={errors.regions?.message}
            />
          )}
        />
      </div>

      <Controller
        name="ethnicities"
        control={control}
        render={({ field }) => (
          <MultiSelect
            label="Ethnicity (optional)"
            options={ETHNICITY_OPTIONS}
            value={field.value}
            onChange={field.onChange}
            placeholder="Select ethnicities"
          />
        )}
      />

        <Controller
          name="interests"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label="Interests *"
              options={INTEREST_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              placeholder="Select interests"
              error={!!errors.interests}
              helperText={errors.interests?.message}
            />
          )}
        />

      <div className="flex gap-4 justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit">Next</Button>
      </div>
    </form>
  );
};
